// Read-only before/transaction/after view model for the CoPilot Solution-card Transactions popup
// (CR-20260619-001). Mirrors the Hedge Matrix "After State Preview" dialog
// (hedging-grid/positions-section/after-state-preview/hg-after-state-preview.component) but READ-ONLY.
//
// Two shapes of Solution reach it, and they are read differently (CR-20260918-001):
//   * A CLOSE (no cards): one solution = one hedge, fully closed. A close leg's action encodes the held
//     direction, so BEFORE = the held legs, TRANSACTION = each leg reversed, AFTER = empty.
//   * An ADJUST PACKAGE (cards): one block per card. A card's legs are ORDERS — an open leg says nothing
//     about what is held — so BEFORE is read from the held-position snapshot taken at Run time for the
//     card's target hedge (empty for a new hedge), TRANSACTION = the card's legs, AFTER = the engine's
//     own target outcome (empty when the card closes the hedge; the legs themselves for a new hedge).
//   ⚠ Feeding a package through the close reading fabricates a Before position out of every To-Open
//     leg and calls every card "closed" — that was the defect this split fixes.
//
// PURE — a function of the Solution + the Run-time held snapshot (no facade/grid read). Reading the
// live working state here would be WRONG once the solution is Previewed (a previewed close nets the
// held leg to zero and drops it; a previewed package already sits on the grid). NO prices (owner ruling
// 2026-06-22 — mirror the reference, which has none). Unit-testable.

import { compareLegStrikeDesc, Holding, Leg, legStrikeForSort, PackageCard, Solution } from './copilot-domain';
import { CoPilotPositionDto } from './copilot-operations';
import { parseOptionTicker } from '../../options-common/options.model';

// One leg line in the Before / After columns (a position; signed qty drives Long/Short).
export interface CopilotTxPositionLeg {
    qty: number;                 // signed held qty (long > 0, short < 0)
    optionType: string | null;
    strike: number | null;
    expiry: string | null;       // ISO "yyyy-MM-dd"
}

// One order line in the Transaction column.
export interface CopilotTxOrder {
    action: string;              // full verbatim action ("Sell To Close" / "Buy To Close" / ...) — same as the card
    qty: number;                 // signed order delta (= held * -1 for a full close)
    expiry: string | null;       // ISO "yyyy-MM-dd"
    strike: number | null;
}

// One hedge's before/transaction/after. A solution = one hedge => one row (MVP), but the grouping below
// keeps this correct for any future multi-hedge content.
export interface CopilotTxRow {
    hedgeId: string | null;
    label: string;               // the owning hedge's display name (no "Close" prefix; never a raw ticker)
    color: string | null;        // the owning hedge's colour (colours the block); null => default text colour
    before: CopilotTxPositionLeg[];
    transaction: CopilotTxOrder[];
    after: CopilotTxPositionLeg[];
}

function orNull<T>(v: T | undefined): T | null {
    return v === undefined ? null : v;
}

// Build the before/transaction/after rows for a Solution's Transactions popup.
//
// `held` is the working-state book as read at Run time (the same positions the run was fired with).
// Only the PACKAGE reading uses it; a close carries its own held snapshot on every leg.
export function buildCopilotTransactionRows(solution: Solution, held?: CoPilotPositionDto[]): CopilotTxRow[] {
    if (!solution || !solution.content || !Array.isArray(solution.content.legs)) {
        return [];
    }

    const cards = solution.content.cards;
    if (Array.isArray(cards) && cards.length > 0) {
        return cards.map(card => buildPackageCardRow(card, held || []));
    }

    return buildCloseRows(solution);
}

// ---- Adjust package: one block per card --------------------------------------------------------

// Signed order quantity, the card's own rule (sell legs negative).
function signedOrderQty(leg: Leg): number {
    return leg.side === 'sell' ? -leg.quantity : leg.quantity;
}

// A leg's decomposed fields: the structured snapshot when present, else parsed back out of the raw
// option ticker (skill-authored legs are stamped at mapping time, but the parse is the safety net).
function legFields(leg: Leg): { optionType: string | null; strike: number | null; expiry: string | null } {
    const parsed = leg ? parseOptionTicker(leg.ticker) : undefined;
    const strike = legStrikeForSort(leg);
    return {
        optionType: (leg.optionType !== undefined && leg.optionType !== null) ? leg.optionType : (parsed ? parsed.type : null),
        strike: isFinite(strike) ? strike : null,
        expiry: (leg.expiry !== undefined && leg.expiry !== null) ? leg.expiry : (parsed ? parsed.expiration : null)
    };
}

function holdingToPositionLeg(h: Holding): CopilotTxPositionLeg {
    const parsed = parseOptionTicker(h.ticker);
    return {
        qty: h.quantity,                                            // SIGNED — a holding carries its own side
        optionType: parsed ? parsed.type : null,
        strike: (parsed && isFinite(parsed.strike)) ? parsed.strike : null,
        expiry: parsed ? parsed.expiration : null
    };
}

function heldToPositionLeg(p: CoPilotPositionDto): CopilotTxPositionLeg {
    return {
        qty: p.quantity,                                            // SIGNED (long > 0, short < 0)
        optionType: orNull(p.optionType),
        strike: orNull(p.strike),
        expiry: orNull(p.expiry)
    };
}

function comparePositionStrikeDesc(a: CopilotTxPositionLeg, b: CopilotTxPositionLeg): number {
    const ka = a.strike === null ? Number.NEGATIVE_INFINITY : a.strike;
    const kb = b.strike === null ? Number.NEGATIVE_INFINITY : b.strike;
    return kb - ka;
}

// The after-state when the engine did not state one: BEFORE netted with the TRANSACTION, keyed by
// contract (type + expiry + strike). Zero-net legs disappear.
function netAfter(before: CopilotTxPositionLeg[], transaction: CopilotTxOrder[], legs: Leg[]): CopilotTxPositionLeg[] {
    const keyOf = (t: string | null, e: string | null, k: number | null) => (t || '') + '|' + (e || '') + '|' + (k === null ? '' : k);
    const order: string[] = [];
    const net: { [key: string]: CopilotTxPositionLeg } = {};
    const add = (p: CopilotTxPositionLeg) => {
        const key = keyOf(p.optionType, p.expiry, p.strike);
        if (!net[key]) {
            net[key] = { qty: 0, optionType: p.optionType, strike: p.strike, expiry: p.expiry };
            order.push(key);
        }
        net[key].qty += p.qty;
    };
    before.forEach(add);
    legs.forEach((leg, i) => {
        const f = legFields(leg);
        add({ qty: transaction[i].qty, optionType: f.optionType, strike: f.strike, expiry: f.expiry });
    });
    return order.map(k => net[k]).filter(p => p.qty !== 0).sort(comparePositionStrikeDesc);
}

function buildPackageCardRow(card: PackageCard, held: CoPilotPositionDto[]): CopilotTxRow {
    const targetId = card.targetHedgeId || null;
    const cardLegs = (card.legs || []).slice().sort(compareLegStrikeDesc);

    // BEFORE: what the target hedge held when the run was fired. A new hedge held nothing.
    const heldLegs = targetId === null ? [] : held.filter(p => p.groupId === targetId);
    const before = heldLegs.map(heldToPositionLeg).sort(comparePositionStrikeDesc);

    // TRANSACTION: the card's own order legs, verbatim action, signed as on the card.
    const transaction: CopilotTxOrder[] = cardLegs.map(leg => {
        const f = legFields(leg);
        return { action: leg.action, qty: signedOrderQty(leg), expiry: f.expiry, strike: f.strike };
    });

    // AFTER: the engine's word first; a close leaves nothing; otherwise net it ourselves.
    let after: CopilotTxPositionLeg[];
    if (card.closesTheHedge === true) {
        after = [];
    } else if (Array.isArray(card.targetOutcome) && card.targetOutcome.length > 0) {
        after = card.targetOutcome.map(holdingToPositionLeg).sort(comparePositionStrikeDesc);
    } else {
        after = netAfter(before, transaction, cardLegs);
    }

    return {
        hedgeId: targetId,
        label: card.title || '',
        color: heldLegs.length > 0 ? orNull(heldLegs[0].color) : null,
        before,
        transaction,
        after
    };
}

// ---- Close: one hedge per solution, fully closed ------------------------------------------------

// A close leg's `side` encodes the original held direction — 'sell' (Sell To Close) was LONG; 'buy'
// (Buy To Close) was SHORT. So:
//   BEFORE      = the held legs (long +qty / short -qty),
//   TRANSACTION = each held leg reversed (long -> Sell, short -> Buy; signed delta = held * -1),
//   AFTER       = empty (the hedge is fully closed).
function buildCloseRows(solution: Solution): CopilotTxRow[] {
    const legs: Leg[] = solution.content.legs;

    // Group legs by their owning hedge (groupId), preserving first-seen order.
    const order: string[] = [];
    const byHedge: { [gid: string]: Leg[] } = {};
    legs.forEach(leg => {
        const gid = leg.groupId || '';
        if (!byHedge[gid]) {
            byHedge[gid] = [];
            order.push(gid);
        }
        byHedge[gid].push(leg);
    });

    return order.map(gid => {
        // Sort the hedge's legs ONCE, strike-DESC (the universal ETS visual rule — see
        // compareLegStrikeDesc), then project Before + Transaction from the same ordered list so the two
        // columns line up row-for-row and match the Solution card's order.
        const hedgeLegs = byHedge[gid].slice().sort(compareLegStrikeDesc);

        const before: CopilotTxPositionLeg[] = hedgeLegs.map(leg => {
            const heldQty = leg.side === 'sell' ? leg.quantity : -leg.quantity;   // sell-to-close was long; buy-to-close was short
            const f = legFields(leg);
            return {
                qty: heldQty,
                optionType: f.optionType,
                strike: f.strike,
                expiry: f.expiry
            };
        });

        const transaction: CopilotTxOrder[] = hedgeLegs.map(leg => {
            const heldQty = leg.side === 'sell' ? leg.quantity : -leg.quantity;
            const f = legFields(leg);
            return {
                action: leg.action,                // full action, same as the Solution card
                qty: heldQty * -1,                 // signed close delta (matches the matrix transQty = -legQty)
                expiry: f.expiry,
                strike: f.strike
            } as CopilotTxOrder;
        });

        // Full close => nothing remains.
        const after: CopilotTxPositionLeg[] = [];

        const head = hedgeLegs[0];

        return {
            hedgeId: gid !== '' ? gid : null,
            // The hedge's own name (no "Close" prefix); fall back to the solution title only if absent.
            label: (head && head.hedgeLabel) ? head.hedgeLabel : ((solution.content && solution.content.displayName) || ''),
            color: head ? orNull(head.hedgeColor) : null,
            before,
            transaction,
            after
        };
    });
}
